import React, { useState, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import {
  Alert,
  Backdrop,
  CircularProgress,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Grid } from "@mui/material";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import { useHistory } from "react-router-dom";
import { Button } from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import "./pin.css";
import {
  isUserIdKioskAuthMode,
  syncStoredKioskAuthMode,
} from "../utils/kioskAuthMode";

const PinAuth = ({ employee, embedded = false }) => {
  const selectedEmployee = employee;
  const [pin, setPin] = useState("");
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [modeSwitchOverlay, setModeSwitchOverlay] = useState({
    open: false,
    title: "",
    description: "",
  });
  const theme = useTheme();

  const PinBox = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
  });

  const PinDigit = styled('div')({
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    border: '1px solid #272525',
    fontSize: '24px',
    fontWeight: 'bold',
    transition: 'border 0.3s, box-shadow 0.3s',

    '&.filled': {
      color: theme.palette.text.primary,
      border: `1px solid ${severity === 'error' ? 'red' : theme.palette.primary.main}`,
    },
    '&.active': {
      border: `3px solid ${theme.palette.primary.main}`,
    },
    '&:hover': {
      cursor: 'pointer',
    },
  });

  const [shake, setShake] = useState(false);
  const history = useHistory();
  const userLogin = useSelector((state) => state.userLogin);
  const { companyInfo } = userLogin;
  const isUserIdLogin = isUserIdKioskAuthMode(selectedEmployee?.loginMode);

  const clearPin = () => {
    setPin("");
  };

  const getModeSwitchOverlayContent = (kioskAuthMode) => {
    if (isUserIdKioskAuthMode(kioskAuthMode)) {
      return {
        title: "Login method changed",
        description: "This branch now uses kiosk User ID login. Switching to the updated login screen...",
      };
    }

    if (kioskAuthMode === "list") {
      return {
        title: "Login method changed",
        description: "This branch now uses employee list login. Switching to the updated login screen...",
      };
    }

    return {
      title: "Something changed",
      description: "Refreshing the kiosk login mode. Please try again.",
    };
  };

  const config = {
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${companyInfo.JWT_TOKEN}`,
    },
  };

  const authenticate = (enteredPin = "") => {
    if (!selectedEmployee || isAuthenticating) {
      return;
    }

    const kioskUserId = selectedEmployee?.kioskUserId?.trim();

    if (isUserIdLogin && !kioskUserId) {
      return;
    }

    setIsAuthenticating(true);

    const url = isUserIdLogin
      ? `${process.env.REACT_APP_API_BACKEND}/api/kiosk/employee-login`
      : `${process.env.REACT_APP_API_BACKEND}/api/kiosk/${selectedEmployee.id}/login`;

    const payload = isUserIdLogin ? { kioskUserId } : { pin: enteredPin };

    axios
      .post(url, payload, config)
      .then((res) => {
        const employeeSession = { data: res.data };
        sessionStorage.setItem("employeeInfo", JSON.stringify(employeeSession));
        setOpen(true);
        setSeverity("success");
        setMessage("Successfully Authenticated");
        setTimeout(() => {
          history.push(`/dashboard/${res.data.employee.id}`);
        }, 1000);
      })
      .catch((err) => {
        const nextKioskAuthMode = err.response?.data?.kioskAuthMode;
        const authModeChanged = syncStoredKioskAuthMode(nextKioskAuthMode);

        if (authModeChanged) {
          const overlayContent = getModeSwitchOverlayContent(nextKioskAuthMode);

          setModeSwitchOverlay({
            open: true,
            title: overlayContent.title,
            description: overlayContent.description,
          });
          setOpen(false);
          setMessage("");
          setSeverity("success");
          setShake(false);
          setPin("");
          setIsAuthenticating(false);

          setTimeout(() => {
            window.location.reload();
          }, 3000);

          return;
        }

        setOpen(true);
        setSeverity("error");
        setShake(true);

        if (err.response && err.response.data && err.response.data.message && err.response.data.message.length > 0) {
          setMessage(err.response.data.message);
        } else {
          setMessage("Something went wrong, please try again later.");
        }

        setTimeout(() => {
          setOpen(false);
          setShake(false);
          setMessage("");
          setSeverity("success");
          setPin("");
          setIsAuthenticating(false);
        }, 2000);
      });
  };
  const addPin = (value) => {
    if (pin.length < 5 && !isAuthenticating) {
      let newPin = pin + value;
      setPin(newPin);

      if (!isUserIdLogin && newPin.length === 5) {
        authenticate(newPin);
      }
    }
  };
  useEffect(() => {
    setPin("");
    setIsAuthenticating(false);
  }, [selectedEmployee]);

  const deletePin = () => {
    let newPin = pin.slice(0, -1);
    setPin(newPin);
  };

  const getAvatarName = (employee) => {
    if (isUserIdLogin) {
      return "ID";
    }

    if (employee.employeeDetail) {
      if (employee.employeeDetail.fname && employee.employeeDetail.lname) {
        return employee.employeeDetail.fname.charAt(0).toUpperCase() + employee.employeeDetail.lname.charAt(0).toUpperCase();
      } else if (employee.employeeDetail.fname) {
        return employee.employeeDetail.fname.charAt(0).toUpperCase();
      } else if (employee.employeeDetail.lname) {
        return employee.employeeDetail.lname.charAt(0).toUpperCase();
      }
    } else {
      return employee.email.charAt(0).toUpperCase();
    }
  }

  const getEmployeeName = (employee) => {
    if (isUserIdLogin) {
      return embedded ? "" : "Kiosk User ID Login";
    }

    if (employee.employeeDetail.fname && employee.employeeDetail.lname) {
      return employee.employeeDetail.fname + " " + employee.employeeDetail.lname;
    } else if (employee.employeeDetail.fname) {
      return employee.employeeDetail.fname;
    } else if (employee.employeeDetail.lname) {
      return employee.employeeDetail.lname;
    } else if (employee.email) {
      return employee.email;
    }
    return "Unknown";
  }

  return (
    <div style={{ margin: embedded ? 0 : "0 10px 0 10px" }}>
      <Grid container spacing={embedded ? 2 : 1} alignItems="center" justifyContent="center">
        <Grid item xs={12}>
          {embedded ? null : (
            <Avatar sx={{ color: theme.palette.background.default, width: 70, height: 70, margin: "auto", fontSize: "2rem", borderRadius: "20%" }}>
              {getAvatarName(selectedEmployee)}
            </Avatar>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant={embedded ? "h5" : "h4"} sx={{ textAlign: "center", margin: "auto" }}>
            {getEmployeeName(selectedEmployee)}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "center", marginTop: embedded ? "2px" : "20px" }}>
          {isUserIdLogin ? (
            <div style={{ marginTop: embedded ? "8px" : "20px" }}>

              <Button
                variant="contained"
                onClick={() => authenticate()}
                disabled={isAuthenticating || !selectedEmployee?.kioskUserId?.trim()}
                sx={{
                  minWidth: embedded ? "100%" : "220px",
                  minHeight: "56px",
                  fontSize: "1rem !important",
                  textTransform: "none",
                }}
              >
                {isAuthenticating ? <CircularProgress size={24} color="inherit" /> : "Sign in"}
              </Button>
            </div>
          ) : (
            <>
              <form autoComplete="off">
                <PinBox className={shake && pin.length === 5 ? 'shake' : ''}>
                  <PinDigit className={pin.length >= 1 ? 'filled' : pin.length === 0 ? 'active' : ''}>{pin[0]}</PinDigit>
                  <PinDigit className={pin.length >= 2 ? 'filled' : pin.length === 1 ? 'active' : ''}>{pin[1]}</PinDigit>
                  <PinDigit className={pin.length >= 3 ? 'filled' : pin.length === 2 ? 'active' : ''}>{pin[2]}</PinDigit>
                  <PinDigit className={pin.length >= 4 ? 'filled' : pin.length === 3 ? 'active' : ''}>{pin[3]}</PinDigit>
                  <PinDigit className={pin.length >= 5 ? 'filled' : pin.length === 4 ? 'active' : ''}>{pin[4]}</PinDigit>
                </PinBox>
              </form>
              <div style={{ marginTop: "20px" }}>
                <div className="btnContainer">
                  <Button
                    onClick={() => {
                      addPin("1");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    1
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("2");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    2
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("3");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    3
                  </Button>
                </div>
                <div className="btnContainer">
                  <Button
                    onClick={() => {
                      addPin("4");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    4
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("5");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    5
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("6");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    6
                  </Button>
                </div>
                <div className="btnContainer">
                  <Button
                    onClick={() => {
                      addPin("7");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    7
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("8");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    8
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("9");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    9
                  </Button>
                </div>
                <div className="btnContainer">
                  <Button
                    onClick={clearPin}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                      textTransform: "capitalize",
                      fontSize: "1rem !important"
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={() => {
                      addPin("0");
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    0
                  </Button>
                  <Button
                    onClick={() => {
                      deletePin();
                    }}
                    sx={{
                      margin: "8px !important",
                      backgroundColor: theme.palette.miscBg.bg,
                      borderRadius: "20% !important",
                      color: theme.palette.text.primary,
                      padding: "5px 10px 5px px",
                      fontSize: "1.5rem !important",
                      height: "70px !important",
                      width: "70px !important",
                      border: "1px solid",
                    }}
                  >
                    <BackspaceOutlinedIcon />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Grid>
      </Grid>
      <Snackbar
        sx={{ marginTop: "45px" }}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={clearPin}
        open={open}
      >
        <Alert severity={severity}>{message}</Alert>
      </Snackbar>

      <Backdrop
        open={modeSwitchOverlay.open}
        sx={{
          color: theme.palette.common.white,
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 10,
          backgroundColor: "rgba(0, 0, 0, 0.82)",
          flexDirection: "column",
          textAlign: "center",
          gap: 2,
          padding: 3,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {modeSwitchOverlay.title}
        </Typography>
        <Typography variant="h6" sx={{ maxWidth: 560, opacity: 0.92 }}>
          {modeSwitchOverlay.description}
        </Typography>
      </Backdrop>
    </div>
  );
};

export default PinAuth;
